// src/pages/ProductsPage.tsx - ÉTAPE 1/4 : Ajout modal sélection quantité
import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import productService, { Product } from '../services/productService';
import cartService from '../services/cartService';
import { ShoppingCart, Search, Filter, Eye, Plus, Minus, X } from 'lucide-react';

interface Category {
  id: number;
  name: string;
  description?: string;
}

// ✅ NOUVEAU: Composant modal pour sélectionner la quantité
interface QuantityModalProps {
  product: Product;
  isOpen: boolean;
  onClose: () => void;
  onAddToCart: (productId: number, quantity: number) => void;
  isLoading: boolean;
}

const QuantityModal = ({ product, isOpen, onClose, onAddToCart, isLoading }: QuantityModalProps) => {
  const [quantity, setQuantity] = useState(1);
  const navigate = useNavigate();

  const handleQuantityChange = (newQuantity: number) => {
    if (newQuantity >= 1 && newQuantity <= product.stock) {
      setQuantity(newQuantity);
    }
  };

  const handleAddToCart = () => {
    onAddToCart(product.id, quantity);
  };

  const handleBuyNow = () => {
    onAddToCart(product.id, quantity);
    onClose();
    // Rediriger vers le panier puis checkout
    navigate('/cart');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-md w-full p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-slate">Ajouter au panier</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="mb-4">
          <div className="flex gap-3">
            <div className="h-16 w-16 bg-gray-100 rounded overflow-hidden flex-shrink-0">
              {product.image_url ? (
                <img 
                  src={productService.getImageUrl(product.image_url)} 
                  alt={product.name}
                  className="h-full w-full object-cover"
                />
              ) : (
                <div className="h-full w-full flex items-center justify-center">
                  <Eye className="h-6 w-6 text-gray-400" />
                </div>
              )}
            </div>
            <div className="flex-1">
              <h4 className="font-medium text-slate">{product.name}</h4>
              <p className="text-terracotta font-semibold">{cartService.formatPrice(product.price)}</p>
              <p className="text-sm text-gray-600">Stock: {product.stock}</p>
            </div>
          </div>
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium text-slate mb-2">
            Quantité
          </label>
          <div className="flex items-center gap-3">
            <button
              onClick={() => handleQuantityChange(quantity - 1)}
              disabled={quantity <= 1}
              className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
            >
              <Minus className="h-4 w-4" />
            </button>
            
            <span className="w-16 text-center font-medium text-lg">
              {quantity}
            </span>
            
            <button
              onClick={() => handleQuantityChange(quantity + 1)}
              disabled={quantity >= product.stock}
              className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
            >
              <Plus className="h-4 w-4" />
            </button>
          </div>
          
          <div className="mt-2 text-right">
            <span className="text-sm text-gray-600">
              Total: <span className="font-semibold text-terracotta">
                {cartService.formatPrice(product.price * quantity)}
              </span>
            </span>
          </div>
        </div>

        <div className="flex gap-3">
          <button
            onClick={handleAddToCart}
            disabled={isLoading}
            className="flex-1 flex items-center justify-center gap-2 bg-olive text-white py-2 px-4 rounded-md hover:bg-terracotta transition duration-300 disabled:opacity-50"
          >
            {isLoading ? (
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
            ) : (
              <ShoppingCart className="h-4 w-4" />
            )}
            Ajouter au panier
          </button>
          
          <button
            onClick={handleBuyNow}
            disabled={isLoading}
            className="flex-1 bg-terracotta text-white py-2 px-4 rounded-md hover:bg-olive transition duration-300 disabled:opacity-50"
          >
            Acheter maintenant
          </button>
        </div>
      </div>
    </div>
  );
};

const ProductsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [sortBy, setSortBy] = useState<'name' | 'price-asc' | 'price-desc' | 'newest'>('name');
  const [addingToCart, setAddingToCart] = useState<number | null>(null);
  const [cartItems, setCartItems] = useState<any[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  
  // ✅ NOUVEAUX: States pour le modal de quantité
  const [quantityModalOpen, setQuantityModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (user?.is_customer) {
      loadCart();
    }
  }, [user]);

  const loadData = async () => {
    try {
      setLoading(true);
      let productsData = user?.is_customer 
        ? await cartService.getAvailableProducts(0, 100)
        : await productService.getProducts(0, 100);
      
      // ✅ NOUVEAU: Filtrer les produits du farmer connecté
      if (user?.is_farmer) {
        productsData = productsData.filter(product => product.owner_id !== user.id);
      }
      
      setProducts(productsData);
      
      const uniqueCategories = Array.from(
        new Set(productsData.map(p => p.category_id))
      ).map(id => ({ id, name: `Catégorie ${id}` }));
      setCategories(uniqueCategories);
      
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCart = async () => {
    if (!user?.is_customer) return;
    
    try {
      const cartData = await cartService.getCart();
      setCartItems(cartData);
    } catch (error) {
      console.error('Erreur lors du chargement du panier:', error);
    }
  };

  // ✅ NOUVEAU: Méthode corrigée avec quantité et proposition checkout
  const handleAddToCart = async (productId: number, quantity: number = 1) => {
    if (!user) {
      alert('Veuillez vous connecter pour ajouter des produits au panier');
      navigate('/login');
      return;
    }

    if (!user.is_customer) {
      alert('Seuls les clients peuvent ajouter des produits au panier');
      return;
    }

    try {
      setAddingToCart(productId);
      
      // ✅ CORRECTION: Utiliser la signature avec quantité
      await cartService.addToCart(productId, quantity);
      
      await loadCart(); // Recharger le panier
      
      // Fermer le modal
      setQuantityModalOpen(false);
      setSelectedProduct(null);
      
      // ✅ NOUVEAU: Notification avec option checkout
      const product = products.find((p: Product) => p.id === productId);
      if (product) {
        const confirmCheckout = confirm(
          `${quantity} x ${product.name} ajouté${quantity > 1 ? 's' : ''} au panier !\n\nVoulez-vous procéder au checkout maintenant ?`
        );
        if (confirmCheckout) {
          navigate('/cart');
        }
      }
    } catch (error) {
      console.error('Erreur lors de l\'ajout au panier:', error);
      alert('Erreur lors de l\'ajout au panier. Veuillez réessayer.');
    } finally {
      setAddingToCart(null);
    }
  };

  // ✅ NOUVEAU: Ouvrir le modal de sélection de quantité
  const openQuantityModal = (product: Product) => {
    if (!user) {
      alert('Veuillez vous connecter pour ajouter des produits au panier');
      navigate('/login');
      return;
    }

    if (!user.is_customer) {
      alert('Seuls les clients peuvent ajouter des produits au panier');
      return;
    }

    setSelectedProduct(product);
    setQuantityModalOpen(true);
  };

  // Vérifier si un produit est dans le panier
  const isProductInCart = (productId: number): boolean => {
    return cartService.isProductInCart(cartItems, productId);
  };

  // Filtrer et trier les produits
  const getFilteredAndSortedProducts = (): Product[] => {
    let filtered = products;

    if (searchTerm) {
      filtered = filtered.filter(product => 
        product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (selectedCategory) {
      filtered = filtered.filter(product => product.category_id === selectedCategory);
    }

    switch (sortBy) {
      case 'price-asc':
        filtered.sort((a, b) => a.price - b.price);
        break;
      case 'price-desc':
        filtered.sort((a, b) => b.price - a.price);
        break;
      case 'newest':
        filtered.sort((a, b) => new Date(b.created_at || '').getTime() - new Date(a.created_at || '').getTime());
        break;
      case 'name':
      default:
        filtered.sort((a, b) => a.name.localeCompare(b.name));
        break;
    }

    return filtered;
  };

  const filteredProducts = getFilteredAndSortedProducts();

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-olive border-r-transparent"></div>
          <p className="mt-2">Chargement des produits...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 bg-beige min-h-screen">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate mb-2">Nos Produits</h1>
          <p className="text-gray-600">Découvrez nos produits frais et locaux</p>
        </div>
        
        {user?.is_customer && (
          <Link 
            to="/cart"
            className="flex items-center gap-2 bg-olive text-white px-4 py-2 rounded-md hover:bg-terracotta transition duration-300"
          >
            <ShoppingCart className="h-5 w-5" />
            Panier ({cartService.calculateCartItemsCount(cartItems)})
          </Link>
        )}
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow-md p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4 mb-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
            <input
              type="text"
              placeholder="Rechercher un produit..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-olive"
            />
          </div>
          
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
          >
            <Filter className="h-5 w-5" />
            Filtres
          </button>
        </div>

        {showFilters && (
          <div className="flex flex-col md:flex-row gap-4 pt-4 border-t border-gray-200">
            <div className="flex-1">
              <label className="block text-sm font-medium text-slate mb-2">Catégorie</label>
              <select
                value={selectedCategory || ''}
                onChange={(e) => setSelectedCategory(e.target.value ? Number(e.target.value) : null)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-olive"
              >
                <option value="">Toutes les catégories</option>
                {categories.map(category => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex-1">
              <label className="block text-sm font-medium text-slate mb-2">Trier par</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as any)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-olive"
              >
                <option value="name">Nom (A-Z)</option>
                <option value="price-asc">Prix croissant</option>
                <option value="price-desc">Prix décroissant</option>
                <option value="newest">Plus récents</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Results Count */}
      <div className="mb-4">
        <p className="text-slate">
          {filteredProducts.length} produit{filteredProducts.length !== 1 ? 's' : ''} trouvé{filteredProducts.length !== 1 ? 's' : ''}
        </p>
      </div>

      {/* Products Grid */}
      {filteredProducts.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow-md">
          <div className="max-w-md mx-auto">
            <div className="h-24 w-24 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
              <Search className="h-8 w-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-slate mb-2">Aucun produit trouvé</h3>
            <p className="text-gray-600 mb-4">
              {user?.is_farmer 
                ? "En tant qu'agriculteur, vous ne pouvez pas acheter vos propres produits. Seuls les produits d'autres agriculteurs sont affichés ici."
                : searchTerm || selectedCategory 
                ? "Essayez de modifier vos critères de recherche." 
                : "Aucun produit disponible pour le moment."
              }
            </p>
            {(searchTerm || selectedCategory) && !user?.is_farmer && (
              <button
                onClick={() => {
                  setSearchTerm('');
                  setSelectedCategory(null);
                }}
                className="bg-olive text-white px-4 py-2 rounded-md hover:bg-terracotta transition duration-300"
              >
                Voir tous les produits
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {filteredProducts.map((product) => (
            <div key={product.id} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300">
              {/* Product Image */}
              <div className="h-48 bg-gray-200 overflow-hidden relative">
                {product.image_url ? (
                  <img 
                    src={productService.getImageUrl(product.image_url)} 
                    alt={product.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-400">
                    <Eye className="h-8 w-8" />
                  </div>
                )}
                
                {/* Stock Badge */}
                {product.stock <= 5 && product.stock > 0 && (
                  <div className="absolute top-2 left-2 bg-orange-500 text-white text-xs px-2 py-1 rounded">
                    Plus que {product.stock}
                  </div>
                )}
                
                {product.stock === 0 && (
                  <div className="absolute top-2 left-2 bg-red-500 text-white text-xs px-2 py-1 rounded">
                    Rupture de stock
                  </div>
                )}

                {/* In Cart Badge */}
                {isProductInCart(product.id) && (
                  <div className="absolute top-2 right-2 bg-olive text-white text-xs px-2 py-1 rounded">
                    Dans le panier
                  </div>
                )}
              </div>

              {/* Product Info */}
              <div className="p-4">
                <h3 className="text-lg font-semibold text-slate mb-2 line-clamp-1">
                  {product.name}
                </h3>
                <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                  {product.description}
                </p>
                
                {/* Price and Stock */}
                <div className="flex justify-between items-center mb-3">
                  <span className="text-xl font-bold text-terracotta">
                    {cartService.formatPrice(product.price)}
                  </span>
                  <span className="text-sm text-gray-500">
                    Stock: {product.stock}
                  </span>
                </div>

                {/* Actions */}
                {user?.is_customer ? (
                  <div className="flex gap-2">
                    {/* ✅ CORRECTION: Route vers /product/:id */}
                    <Link
                      to={`/product/${product.id}`}
                      className="flex-1 text-center bg-gray-200 text-slate px-4 py-2 rounded-md hover:bg-gray-300 transition duration-300"
                    >
                      Voir détails
                    </Link>
                    {/* ✅ NOUVEAU: Bouton ouvre le modal de quantité */}
                    <button
                      onClick={() => openQuantityModal(product)}
                      disabled={product.stock <= 0 || addingToCart === product.id}
                      className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md transition duration-300 ${
                        product.stock > 0
                          ? 'bg-olive text-white hover:bg-terracotta'
                          : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      }`}
                    >
                      {addingToCart === product.id ? (
                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                      ) : (
                        <ShoppingCart className="h-4 w-4" />
                      )}
                      {product.stock > 0 ? 'Ajouter' : 'Indisponible'}
                    </button>
                  </div>
                ) : (
                  <div className="text-center py-2">
                    <Link 
                      to="/login"
                      className="text-olive hover:text-terracotta transition duration-300"
                    >
                      Connectez-vous pour acheter
                    </Link>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ✅ NOUVEAU: Modal de sélection de quantité */}
      {selectedProduct && (
        <QuantityModal
          product={selectedProduct}
          isOpen={quantityModalOpen}
          onClose={() => {
            setQuantityModalOpen(false);
            setSelectedProduct(null);
          }}
          onAddToCart={handleAddToCart}
          isLoading={addingToCart === selectedProduct.id}
        />
      )}

      {/* Load More Button */}
      {filteredProducts.length >= 20 && (
        <div className="text-center mt-8">
          <button className="bg-olive text-white px-6 py-3 rounded-md hover:bg-terracotta transition duration-300">
            Voir plus de produits
          </button>
        </div>
      )}
    </div>
  );
};

export default ProductsPage;